import { Suspense } from 'react';
import {
  useProduct,
  useSelectedVariant
} from '@/components/product/product-context';
import { SKELETON_PROPS } from '@/lib/constants/skeleton';
import { CheckboxSVG } from './Icons';
import ColumnSelectorHeader from './ColumnSelectorHeader';
import HelpModalLink from './HelpModalLink';
import OptionCard from './OptionCard';
import {
  resolveColumnOption,
  selectedOptionValue
} from './resolve-column-option';
import { safePriceDifference } from './calculate-price-difference';
import { useColumnOptionClick } from './use-column-option-click';
import { useVariantHelpModal } from './use-variant-help-modal';

const INPUT_ID = 'detailed-variant-checkbox-selector';

/**
 * A selector that is actually a single checkbox with a label and description.
 * It allows for a modal and shows a price difference between the lowest cost
 * variant in the same size.
 *
 * This is meant to be used with single upgrade scenarios.
 */
export default function CheckboxSelector({ settings }) {
  const { productData } = useProduct();
  const helpModal = useVariantHelpModal(settings);

  // This component has a lot more required settings than most selectors,
  // so validate all the critical settings and context before proceeding
  const result = resolveColumnOption(settings, productData);
  if (!result.ok) {
    console.warn(`[CheckboxSelector] ${result.error}`);
    return null;
  }

  const { optionName, option, columnSettings, baseOptionValue } = result.data;

  const upgradeOptionValue = option.values.find((o) => o !== baseOptionValue);
  if (!upgradeOptionValue) {
    console.warn('[CheckboxSelector] Could not find an upgrade option');
    return null;
  }

  const optionCopy = columnSettings.option_copy?.find(
    (o) => o?.option_value === upgradeOptionValue
  );
  const card = {
    optionName,
    upgradeOptionValue,
    title: optionCopy?.title ?? upgradeOptionValue,
    description: optionCopy?.description
  };

  return (
    <>
      <ColumnSelectorHeader
        label={settings?.label ?? optionName}
        helpModal={helpModal}
      />
      <Suspense fallback={<UpgradeCard card={card} {...SKELETON_PROPS} />}>
        <SelectedUpgradeCard
          card={card}
          option={option}
          baseOptionValue={baseOptionValue}
        />
      </Suspense>
      <HelpModalLink helpModal={helpModal} position="Bottom Left" />
    </>
  );
}

/**
 * Reads the selected variant and selection actions, so it needs to be wrapped
 * in a Suspense tag. Renders the same card as the fallback, with the checked
 * state, the price and the click handler filled in.
 */
const SelectedUpgradeCard = ({ card, option, baseOptionValue }) => {
  const { productData } = useProduct();
  const { variant } = useSelectedVariant();
  const selectOption = useColumnOptionClick();

  const isChecked = selectedOptionValue(variant, option) !== baseOptionValue;

  return (
    <UpgradeCard
      card={card}
      checked={isChecked}
      priceDifference={safePriceDifference(
        'CheckboxSelector',
        productData?.shopifyProduct,
        variant,
        baseOptionValue,
        card.upgradeOptionValue,
        option
      )}
      onToggle={() =>
        selectOption(isChecked ? baseOptionValue : card.upgradeOptionValue)
      }
      data-cy="column-checkbox-selector"
    />
  );
};

/**
 * The upgrade card itself, presentational stuff.
 */
const UpgradeCard = ({
  card,
  checked = false,
  priceDifference,
  onToggle,
  ...cardProps
}) => (
  <OptionCard
    selected={checked}
    onClick={onToggle}
    className="mt-2 mb-3"
    title={card.title}
    titleFor={INPUT_ID}
    titleClassName="pointer-events-none"
    description={card.description}
    priceDifference={priceDifference}
    control={
      <>
        <input
          id={INPUT_ID}
          type="checkbox"
          checked={checked}
          name={card.optionName}
          value={card.upgradeOptionValue}
          className="sr-only"
          readOnly
          tabIndex="-1"
        />
        <CheckboxSVG checked={checked} />
      </>
    }
    {...cardProps}
  />
);
